import { useCallback, useEffect, useState } from "react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { Button } from "../../primitives/button";
import { Input } from "../../primitives/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../primitives/select";
import {
  EntityCombobox,
  type EntityOption,
} from "../../patterns/EntityCombobox";
import { X, Phone, Mail, Users, FileText } from "lucide-react";
import type * as API from "@shared-core/data/api-types";
import {
  format,
  addDays,
  endOfWeek,
  addWeeks,
  setHours,
  setMinutes,
} from "date-fns";
import { PRIORITY_CONFIG, type TicketPriority } from "./TicketMetadataStrip";
import { currentTime } from "@shared-core/domain/current-time";

const NO_CATEGORY_VALUE = "__none__";

// Due-date quick-presets pick a day but need a time too now that due dates
// carry one — 9am is a reasonable default working-hours reminder time.
function atNineAm(date: Date): Date {
  return setMinutes(setHours(date, 9), 0);
}

type LinkType = "none" | "alert" | "opportunity" | "job" | "estimate";

const LINK_TYPE_LABELS: Record<LinkType, string> = {
  none: "None",
  alert: "Alert",
  opportunity: "Opportunity",
  job: "Job",
  estimate: "Estimate",
};

// Create-only: viewing and editing an existing ticket happen on
// TicketDetailPage, not in a modal.
interface TicketModalProps {
  onClose: () => void;
  onSave: (data: {
    title: string;
    description: string;
    actionType: ActionType;
    dueDate: string;
    priority: TicketPriority;
    categoryId: string | null;
    customerId: string | null;
    assignedToUserId: string | null;
    departmentSlug: string | null;
    customerPersonId: string | null;
    subjectType: Exclude<LinkType, "none"> | null;
    subjectId: string | null;
    subjectLabel: string | null;
  }) => void;
  isSubmitting?: boolean;
}

type ActionType = "call" | "email" | "meeting" | "general";

const ACTION_TYPE_CONFIG = {
  general: { label: "General", icon: FileText },
  call: { label: "Call", icon: Phone },
  email: { label: "Email", icon: Mail },
  meeting: { label: "Meeting", icon: Users },
};

export function TicketModal({
  onClose,
  onSave,
  isSubmitting = false,
}: TicketModalProps) {
  const dataAdapter = useDataAdapter();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState(
    format(atNineAm(currentTime()), "yyyy-MM-dd'T'HH:mm"),
  );
  const [actionType, setActionType] = useState<ActionType>("general");

  // Create-mode-only fields — every field the Ticket model supports at
  // creation time (see workflow_module/models/ticket.py), not just the
  // bare title/description/due-date/action-type this form started with.
  const [createPriority, setCreatePriority] =
    useState<TicketPriority>("normal");
  const [createCategoryId, setCreateCategoryId] = useState<string | null>(null);
  const [createCustomer, setCreateCustomer] = useState<EntityOption | null>(
    null,
  );
  const [createAssignee, setCreateAssignee] = useState<EntityOption | null>(
    null,
  );
  // Ticket owner — a person (default, unchanged from prior behaviour) or a
  // department fallback (department-ticket-routing). Mutually exclusive:
  // switching modes clears whichever field the other mode owns, so the
  // submitted payload never carries both (INV-1 in workflow_module's
  // create_ticket — an assignee always "wins" ownership over a department).
  const [ownerMode, setOwnerMode] = useState<"person" | "department">("person");
  const [createDepartment, setCreateDepartment] =
    useState<API.Department | null>(null);
  // Active departments only — an inactive department has no lead to
  // escalate to, so offering one in the picker would be a trap.
  const [activeDepartments, setActiveDepartments] = useState<API.Department[]>(
    [],
  );
  // Optional — "pick a contact if relevant, don't force it" (most tickets
  // aren't about one specific person). Scoped to the chosen customer's own
  // contacts so the search never has to work across every contact in CIS;
  // same customer-gating pattern as the job/alert/opportunity link below.
  const [createContact, setCreateContact] = useState<EntityOption | null>(null);
  const [linkType, setLinkType] = useState<LinkType>("none");
  const [linkedEntity, setLinkedEntity] = useState<EntityOption | null>(null);
  const linkNeedsCustomer =
    linkType === "job" || linkType === "alert" || linkType === "opportunity";

  // A newly-picked customer (or a changed link type) invalidates whatever
  // was previously linked — it may not belong to the new customer, or may
  // not even be the right kind of entity any more.
  useEffect(() => {
    setLinkedEntity(null);
  }, [createCustomer, linkType]);

  // A newly-picked customer also invalidates any already-chosen contact —
  // they belong to the previous customer, not this one.
  useEffect(() => {
    setCreateContact(null);
  }, [createCustomer]);

  const [categories, setCategories] = useState<API.TicketCategory[]>([]);

  useEffect(() => {
    let cancelled = false;
    async function loadPickerOptions() {
      const [categoryList, departmentList] = await Promise.all([
        dataAdapter.listTicketCategories(),
        dataAdapter.listDepartments(true),
      ]);
      if (cancelled) return;
      setCategories(categoryList);
      setActiveDepartments(departmentList);
    }
    loadPickerOptions();
    return () => {
      cancelled = true;
    };
  }, [dataAdapter]);

  const fetchCustomers = useCallback(
    async (q: string): Promise<EntityOption[]> => {
      const resp = await dataAdapter.getCustomersForIssuePicker({
        q: q || undefined,
        page: 1,
        pageSize: 25,
      });
      return resp.items.map((c) => ({ id: c.id, label: c.name }));
    },
    [dataAdapter],
  );

  const fetchAssignees = useCallback(
    async (q: string): Promise<EntityOption[]> => {
      const resp = await dataAdapter.listUsers({
        q: q || undefined,
        page: 1,
        pageSize: 25,
      });
      return resp.items.map((u) => ({
        id: u.id,
        label: u.name,
        hint: u.email,
      }));
    },
    [dataAdapter],
  );

  // Every person this customer has on file (CIS people at the customer —
  // the same data the Contacts page and customer-360 Contacts box use),
  // searched server-side. Gated on a customer being chosen first: without that, the
  // search would run across every contact in CIS, which for a customer
  // with hundreds of contacts would make typeahead nearly useless.
  const fetchContacts = useCallback(
    async (q: string): Promise<EntityOption[]> => {
      if (!createCustomer) return [];
      const resp = await dataAdapter.getContactViews({
        customerId: createCustomer.id,
        q: q || undefined,
        page: 1,
        pageSize: 25,
      });
      return resp.items.map((c) => ({
        id: String(c.id),
        label: c.name,
        hint: c.email_address || c.phone_number_1 || undefined,
      }));
    },
    [dataAdapter, createCustomer],
  );

  // Job/Alert/Opportunity links are scoped to the chosen customer (the same
  // gating RaiseIssuePage uses for its job picker); Estimate is a global
  // search since an enquiry isn't picked by customer here.
  const fetchLinkOptions = useCallback(
    async (q: string): Promise<EntityOption[]> => {
      if (linkType === "job") {
        if (!createCustomer) return [];
        const resp = await dataAdapter.getJobsForIssuePicker(
          createCustomer.id,
          { q: q || undefined, page: 1, pageSize: 25 },
        );
        return resp.items.map((j) => ({
          id: j.job_no,
          label: j.job_no,
          hint: j.title,
        }));
      }
      if (linkType === "alert") {
        if (!createCustomer) return [];
        const resp = await dataAdapter.getAlerts({
          customerId: createCustomer.id,
          q: q || undefined,
          page: 1,
          pageSize: 25,
        });
        return resp.items.map((a) => ({
          id: a.id,
          label: a.title,
          hint: a.alert_family,
        }));
      }
      if (linkType === "opportunity") {
        if (!createCustomer) return [];
        const resp = await dataAdapter.getOpportunities({
          customerId: createCustomer.id,
          q: q || undefined,
          page: 1,
          pageSize: 25,
        });
        return resp.items.map((o) => ({
          id: o.id,
          label: o.title,
          hint: o.due_date ?? undefined,
        }));
      }
      if (linkType === "estimate") {
        const items = await dataAdapter.searchEstimateSources(q, 20);
        return items
          .filter((s) => s.kind === "estimate" && !!s.estimate_header_ref)
          .map((s) => ({
            id: s.estimate_header_ref as string,
            label: `${s.ref} · ${s.title || "(untitled)"}`,
            hint: s.customer_name ?? undefined,
          }));
      }
      return [];
    },
    [dataAdapter, linkType, createCustomer],
  );

  const handleSubmit = () => {
    if (!title.trim()) return;
    if (linkType !== "none" && !linkedEntity) return;
    // Department mode with nothing picked must never fall through to the
    // omitted-assignedToUserId default (silently self-assigning) — the user
    // explicitly asked for department routing, so an unpicked department
    // blocks submission rather than degrading into "assigned to me".
    if (ownerMode === "department" && !createDepartment) return;

    onSave({
      title: title.trim(),
      description: description.trim(),
      actionType,
      dueDate,
      priority: createPriority,
      categoryId: createCategoryId,
      customerId: createCustomer?.id ?? null,
      assignedToUserId:
        ownerMode === "person" ? (createAssignee?.id ?? null) : null,
      departmentSlug:
        ownerMode === "department" ? (createDepartment?.slug ?? null) : null,
      customerPersonId: createContact?.id ?? null,
      subjectType: linkType === "none" ? null : linkType,
      subjectId: linkType === "none" ? null : (linkedEntity?.id ?? null),
      subjectLabel: linkType === "none" ? null : (linkedEntity?.label ?? null),
    });
  };

  const ActionIcon = ACTION_TYPE_CONFIG[actionType].icon;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-background border border-border rounded-lg shadow-lg w-full max-w-3xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="shrink-0 px-6 py-4 border-b border-border">
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-lg">{title || "Untitled Ticket"}</h2>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="size-4" />
            </Button>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-6">
          <div className="space-y-4 pb-5 border-b border-border">
            <div>
              <label htmlFor="ticket-title" className="block text-sm mb-1.5">
                Ticket Title *
              </label>
              <Input
                type="text"
                id="ticket-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter ticket title"
                className="text-sm"
                autoFocus
              />
            </div>
            <div>
              <label
                htmlFor="ticket-description"
                className="block text-sm mb-1.5"
              >
                Description
              </label>
              <textarea
                id="ticket-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Add more context (optional)"
                rows={3}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="ticket-due-date"
                  className="block text-sm mb-1.5"
                >
                  Due Date
                </label>
                <Input
                  type="datetime-local"
                  id="ticket-due-date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="text-sm"
                />
                <div className="flex gap-1.5 mt-1.5 flex-wrap">
                  {[
                    { label: "Today", date: atNineAm(currentTime()) },
                    {
                      label: "Tomorrow",
                      date: atNineAm(addDays(currentTime(), 1)),
                    },
                    {
                      label: "End of week",
                      date: atNineAm(
                        endOfWeek(currentTime(), { weekStartsOn: 1 }),
                      ),
                    },
                    {
                      label: "End of next week",
                      date: atNineAm(
                        endOfWeek(addWeeks(currentTime(), 1), {
                          weekStartsOn: 1,
                        }),
                      ),
                    },
                  ].map((preset) => (
                    <button
                      key={preset.label}
                      type="button"
                      onClick={() =>
                        setDueDate(format(preset.date, "yyyy-MM-dd'T'HH:mm"))
                      }
                      className={`px-2 py-0.5 rounded text-xs transition-colors ${
                        dueDate === format(preset.date, "yyyy-MM-dd'T'HH:mm")
                          ? "bg-primary text-white"
                          : "bg-muted text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label
                  htmlFor="ticket-action-type"
                  className="block text-sm mb-1.5"
                >
                  Action Type
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <ActionIcon className="size-4 text-muted-foreground" />
                  </div>
                  <select
                    id="ticket-action-type"
                    value={actionType}
                    onChange={(e) =>
                      setActionType(e.target.value as ActionType)
                    }
                    className="w-full pl-10 pr-3 py-2 border border-input rounded-md bg-background text-sm appearance-none cursor-pointer"
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                      backgroundPosition: "right 0.5rem center",
                      backgroundRepeat: "no-repeat",
                      backgroundSize: "1.5em 1.5em",
                    }}
                  >
                    {Object.entries(ACTION_TYPE_CONFIG).map(([key, config]) => (
                      <option key={key} value={key}>
                        {config.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm mb-1.5">Priority</label>
                <Select
                  value={createPriority}
                  onValueChange={(v) => setCreatePriority(v as TicketPriority)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(PRIORITY_CONFIG) as TicketPriority[]).map(
                      (p) => (
                        <SelectItem key={p} value={p}>
                          {PRIORITY_CONFIG[p].label}
                        </SelectItem>
                      ),
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm mb-1.5">Category</label>
                <Select
                  value={createCategoryId ?? NO_CATEGORY_VALUE}
                  onValueChange={(v) =>
                    setCreateCategoryId(v === NO_CATEGORY_VALUE ? null : v)
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="No category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_CATEGORY_VALUE}>
                      No category
                    </SelectItem>
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm mb-1.5">
                  Customer (optional)
                </label>
                <EntityCombobox
                  value={createCustomer}
                  onChange={setCreateCustomer}
                  fetchOptions={fetchCustomers}
                  placeholder="Search customers…"
                  ariaLabel="Customer this ticket relates to"
                  className="w-full"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-sm">Owner</label>
                  <div
                    role="tablist"
                    aria-label="Route this ticket to a person or a department"
                    className="inline-flex rounded-md border border-border overflow-hidden"
                  >
                    {(
                      [
                        { id: "person", label: "Person" },
                        { id: "department", label: "Department" },
                      ] as const
                    ).map((tab, i) => {
                      const active = ownerMode === tab.id;
                      return (
                        <button
                          key={tab.id}
                          type="button"
                          role="tab"
                          aria-selected={active}
                          onClick={() => {
                            if (active) return;
                            setOwnerMode(tab.id);
                            // Mutually exclusive — the mode not chosen
                            // must not silently ride along in the
                            // submitted payload.
                            if (tab.id === "person") {
                              setCreateDepartment(null);
                            } else {
                              setCreateAssignee(null);
                            }
                          }}
                          className={`px-2 py-1 text-xs transition-colors ${
                            i > 0 ? "border-l border-border" : ""
                          } ${
                            active
                              ? "bg-primary text-white"
                              : "bg-surface text-foreground hover:bg-surface-hover"
                          }`}
                        >
                          {tab.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                {ownerMode === "person" ? (
                  <EntityCombobox
                    value={createAssignee}
                    onChange={setCreateAssignee}
                    fetchOptions={fetchAssignees}
                    placeholder="Defaults to you"
                    ariaLabel="Assign this ticket to"
                    className="w-full"
                  />
                ) : activeDepartments.length === 0 ? (
                  <p className="text-xs text-muted-foreground py-2">
                    No departments are configured yet — an admin needs to set
                    one up before this ticket can be routed.
                  </p>
                ) : (
                  <Select
                    value={createDepartment?.slug ?? ""}
                    onValueChange={(v) =>
                      setCreateDepartment(
                        activeDepartments.find((d) => d.slug === v) ?? null,
                      )
                    }
                  >
                    <SelectTrigger
                      className="w-full"
                      aria-label="Department to route this ticket to"
                    >
                      <SelectValue placeholder="Select a department…" />
                    </SelectTrigger>
                    <SelectContent>
                      {activeDepartments.map((d) => (
                        <SelectItem key={d.id} value={d.slug}>
                          {d.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm mb-1.5">Contact (optional)</label>
              <EntityCombobox
                value={createContact}
                onChange={setCreateContact}
                fetchOptions={fetchContacts}
                placeholder={
                  createCustomer
                    ? "Search this customer's contacts…"
                    : "Select a customer first"
                }
                ariaLabel="Contact this ticket relates to"
                disabled={!createCustomer}
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm mb-1.5">Link to (optional)</label>
              <div className="flex gap-2">
                <Select
                  value={linkType}
                  onValueChange={(v) => setLinkType(v as LinkType)}
                >
                  <SelectTrigger className="w-[160px] shrink-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(LINK_TYPE_LABELS) as LinkType[]).map((lt) => (
                      <SelectItem key={lt} value={lt}>
                        {LINK_TYPE_LABELS[lt]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {linkType !== "none" && (
                  <div className="flex-1">
                    <EntityCombobox
                      value={linkedEntity}
                      onChange={setLinkedEntity}
                      fetchOptions={fetchLinkOptions}
                      placeholder={
                        linkNeedsCustomer && !createCustomer
                          ? "Select a customer first"
                          : `Search ${LINK_TYPE_LABELS[linkType].toLowerCase()}s…`
                      }
                      ariaLabel={`Pick a ${linkType} to link`}
                      disabled={linkNeedsCustomer && !createCustomer}
                    />
                  </div>
                )}
              </div>
              {linkNeedsCustomer && !createCustomer && (
                <p className="text-xs text-muted-foreground mt-1">
                  Choose a customer above to search their{" "}
                  {LINK_TYPE_LABELS[linkType].toLowerCase()}s
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="shrink-0 px-6 py-4 border-t border-border flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              !title.trim() ||
              isSubmitting ||
              (linkType !== "none" && !linkedEntity) ||
              (ownerMode === "department" && !createDepartment)
            }
          >
            {isSubmitting ? "Adding..." : "Add Ticket"}
          </Button>
        </div>
      </div>
    </div>
  );
}
